import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, pairwise, ReplaySubject, startWith } from 'rxjs';

import { GeneralSettings, PageNavigationItem, PortalConfiguration, WidgetConfigMap, WidgetIdentifier } from '@celum/portals/domain';

import { ConfigurationResolver } from './configuration-resolver';
import { PortalNavigationService } from './portal-navigation.service';
import { ConfigurationService } from '../model/configuration.service';
import { getPageFromPortalConfiguration } from '../util/configuration.util';
import { getFallbackPage } from '../util/navigation-configuration.util';

@Injectable()
export class ReadonlyConfigurationService implements ConfigurationService {
  public generalSettings$ = new BehaviorSubject<GeneralSettings>(null);
  public portalConfiguration$ = new BehaviorSubject<PortalConfiguration>(null);
  public portalConfiguration = toSignal(this.portalConfiguration$);
  public widgets$ = new ReplaySubject<WidgetIdentifier[]>(1);
  public widgetConfigs$ = new BehaviorSubject<WidgetConfigMap>(null);
  public widgetConfigs = toSignal(this.widgetConfigs$);
  public portalId$ = new BehaviorSubject<string>(null);
  public activePageId$ = new BehaviorSubject<string>(null);
  public activePageId = toSignal(this.activePageId$);
  public activePage$ = new BehaviorSubject<PageNavigationItem>(null);
  public activePage = toSignal(this.activePage$);
  public previousPageSlug$ = new BehaviorSubject<string>(null);

  private navigationService = inject(PortalNavigationService);
  private configurationResolver = inject(ConfigurationResolver);
  public requestedContainerId = this.navigationService.requestedContainerId;

  constructor() {
    const config = this.configurationResolver.configuration$.getValue();

    this.portalId$.next(config.id);
    this.portalConfiguration$.next(config);
    this.generalSettings$.next(config.configuration.general.settings);
    this.widgetConfigs$.next(config.configuration.widgetMap);

    this.activePage$.pipe(startWith(null), pairwise()).subscribe(([previousConfig]) => this.previousPageSlug$.next(previousConfig?.slug ?? previousConfig?.id));

    this.navigationService.activePageId$.subscribe(activePageId => {
      const portalConfiguration = this.portalConfiguration$.getValue();

      const activeNavigationItem = getPageFromPortalConfiguration(portalConfiguration, activePageId);

      const itemWidgets = activeNavigationItem?.discoverable
        ? getFallbackPage(portalConfiguration.configuration.navigation).widgets
        : activeNavigationItem?.widgets;

      this.activePageId$.next(activeNavigationItem?.id);
      this.activePage$.next(activeNavigationItem);
      this.widgets$.next([
        ...(portalConfiguration.configuration.general.widgets.header ? [portalConfiguration.configuration.general.widgets.header] : []),
        ...(itemWidgets ?? [])
      ]);
    });
  }

  public selectWidget<C>(widgetId: string, options?: C): void {
    console.warn('You are not in edit mode! "selectWidget" does nothing in readonly mode!');
  }

  public clearSelectedWidget(): void {
    console.warn('You are not in edit mode! "clearSelectedWidget" does nothing in readonly mode!');
  }

  public getPortalId(): string {
    return this.portalId$.value;
  }
}
